import { localToday } from './flightFilters';
import { collection, doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { ref as storageRef, uploadString } from 'firebase/storage';
import { auth, db, storage } from './firebase';
import type { LogbookEntryDoc } from './types';

// The hour columns, in logbook order — drives the traditional table + the totals.
export const HOUR_FIELDS: { key: keyof LogbookEntryDoc; label: string; short: string }[] = [
  { key: 'asel', label: 'Single-Engine Land', short: 'SEL' },
  { key: 'amel', label: 'Multi-Engine Land', short: 'MEL' },
  { key: 'ases', label: 'Single-Engine Sea', short: 'SES' },
  { key: 'ames', label: 'Multi-Engine Sea', short: 'MES' },
  { key: 'night', label: 'Night', short: 'Night' },
  { key: 'actualInstrument', label: 'Actual Instrument', short: 'Actual' },
  { key: 'simInstrument', label: 'Simulated Instr (Hood)', short: 'Hood' },
  { key: 'simulator', label: 'Flight Simulator', short: 'Sim' },
  { key: 'crossCountry', label: 'Cross Country', short: 'X-C' },
  { key: 'solo', label: 'Solo', short: 'Solo' },
  { key: 'dualReceived', label: 'Dual Received', short: 'Dual' },
  { key: 'pic', label: 'Pilot in Command', short: 'PIC' },
  { key: 'sic', label: 'Second in Command', short: 'SIC' },
  { key: 'instructorGiven', label: 'Instructor Given', short: 'CFI' },
];

const n = (v: unknown) => (typeof v === 'number' && isFinite(v) ? v : 0);

// Running totals across a set of entries (for the totals bar + page totals).
export function computeTotals(entries: LogbookEntryDoc[]) {
  const t = {
    total: 0, pic: 0, crossCountry: 0, night: 0, instrument: 0, dualReceived: 0,
    solo: 0, landings: 0,
  };
  for (const e of entries) {
    t.total += n(e.total);
    t.pic += n(e.pic);
    t.crossCountry += n(e.crossCountry);
    t.night += n(e.night);
    t.instrument += n(e.actualInstrument) + n(e.simInstrument);
    t.dualReceived += n(e.dualReceived);
    t.solo += n(e.solo);
    t.landings += n(e.dayLandings) + n(e.nightLandings);
  }
  return t;
}

// Validate one entry's internal math. Returns a flag + a human note if something
// can't be true (bad OCR / handwriting / mis-entry) so the user fixes it.
export function validateEntry(e: Partial<LogbookEntryDoc>): { needsReview: boolean; reviewNote: string } {
  const total = n(e.total);
  const issues: string[] = [];
  if (total <= 0) issues.push('total time is missing');
  const overTotal = (label: string, v: number) => {
    if (total > 0 && v > total + 0.05) issues.push(`${label} (${v.toFixed(1)}) exceeds total (${total.toFixed(1)})`);
  };
  overTotal('night', n(e.night));
  overTotal('instrument', n(e.actualInstrument) + n(e.simInstrument));
  overTotal('cross-country', n(e.crossCountry));
  overTotal('PIC', n(e.pic));
  overTotal('dual', n(e.dualReceived));
  overTotal('solo', n(e.solo));
  const cat = n(e.asel) + n(e.amel) + n(e.ases) + n(e.ames);
  if (total > 0 && cat > 0 && Math.abs(cat - total) > 0.1)
    issues.push(`category time (${cat.toFixed(1)}) doesn't match total (${total.toFixed(1)})`);
  return { needsReview: issues.length > 0, reviewNote: issues.join('; ') };
}

export function blankEntry(ownerUid: string): Partial<LogbookEntryDoc> {
  return { ownerUid, date: localToday(), source: 'manual' };
}

// Read a photographed logbook spread (left+right = 2 images). Drops the photos into
// a `logbookCaptures` doc; the onLogbookCaptureCreated Firebase trigger reads them
// with Claude (deep thinking, no 26s HTTP timeout) and writes the rows back. We
// listen for status:'done'. This is why a thorough read can take ~30s and not fail.
export interface LogbookReadResult {
  entries: Partial<LogbookEntryDoc>[];
  pageTotal: number | null; // the page's printed Page-Total subtotal, for cross-check
}

export async function extractLogbook(images: string[], mimeType: string): Promise<LogbookReadResult> {
  if (!db || !storage) throw new Error('Not connected.');
  const uid = auth?.currentUser?.uid;
  if (!uid) throw new Error('Sign in required.');
  // Photos go to Storage (no 1MB doc limit); the doc only holds their paths.
  const ref = doc(collection(db, 'logbookCaptures'));
  const imagePaths: string[] = [];
  for (let i = 0; i < images.length; i++) {
    const p = `logbook-captures/${uid}/${ref.id}/${i}.jpg`;
    await uploadString(storageRef(storage, p), images[i], 'base64', { contentType: mimeType || 'image/jpeg' });
    imagePaths.push(p);
  }
  await setDoc(ref, {
    ownerUid: uid,
    imagePaths,
    mimeType: mimeType || 'image/jpeg',
    status: 'pending',
    createdAt: serverTimestamp(),
  });
  return await new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      unsub();
      reject(new Error('Reading took too long — try again.'));
    }, 285000);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const d = snap.data() as { status?: string; entries?: Partial<LogbookEntryDoc>[]; pageTotal?: number | null; error?: string } | undefined;
        if (!d) return;
        if (d.status === 'done') {
          clearTimeout(timer);
          unsub();
          resolve({ entries: Array.isArray(d.entries) ? d.entries : [], pageTotal: typeof d.pageTotal === 'number' ? d.pageTotal : null });
        } else if (d.status === 'error') {
          clearTimeout(timer);
          unsub();
          reject(new Error(d.error || 'Could not read the logbook.'));
        }
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}
